import { DayDate } from "@cmp-src/data-display/dates/DayDate";
import { SelectItem, UnifiedSelect } from "@cmp-src/form/UnifiedSelect";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "@cmp-src/primitives/tooltip";
import {
  AgendaDay as AgendaDayType,
  AgendaSchedule,
  DivisionsTree,
  IDedicatedResourceRef,
  ISchedule,
  IWorkingLocation,
  IWorkingLocationPresenceType,
  Office,
  OfficeAvailability,
  parseDateOnlyString,
  WL_PRESENCE_OFFICE,
  WL_PRESENCE_OOO,
  WL_PRESENCE_REMOTE
} from "@gettactic/api";
import { ReservationCard } from "@gettactic/components/src/components/reservation-card/ReservationCard";
import { HomeIcon } from "@gettactic/components/src/icons/TacticIcons/HomeIcon";
import { OfficeIcon } from "@gettactic/components/src/icons/TacticIcons/OfficeIcon";
import { OOOIcon } from "@gettactic/components/src/icons/TacticIcons/OOOIcon";
import { SETTINGS_ROUTES } from "@gettactic/defines/src/web-routes";
import { buildUrl, capitalize, cn, parseApiDate } from "@gettactic/helpers";
import {
  useAuthenticated,
  useDivisionsMenuQuery,
  useWorkplaceRequestsAllowed
} from "@gettactic/hooks";
import { format, isPast, isToday } from "date-fns";
import { ChevronDownIcon, ChevronRight, Plus } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useMapModalContext } from "@/features/map/components/modal";
import { getMapModalTab } from "@/features/map/helpers/getMapModalTab";
import { useReserveModal } from "@/features/reserve/hooks/useReserveModal";
import { useReserve } from "@/features/reserve/providers/context";
import { getDuplicateReservationData } from "@/features/reserve/providers/getDuplicateReservationData";
import { api } from "@/lib/api/api";
import { scheduleToOfficeWallClock } from "@/lib/scheduleToOfficeWallClock";
import { useRouter } from "@/lib/useRouter";
import { isDeskDedicatedOn } from "../lib/dedicatedDeskDays";
import {
  isDedicatedDeskSelection,
  selectDedicatedDesk
} from "../lib/dedicatedDeskSelection";
import { officeToday } from "../lib/reservationsElsewhere";
import { CheckInCardOnWeb } from "./CheckInCardOnWeb";
import { DedicatedDeskCard } from "./DedicatedDeskCard";
import { VisitCard } from "./VisitCard";

interface OptimisticLocation extends Partial<IWorkingLocation> {
  location: IWorkingLocationPresenceType;
  office_id?: string;
}

// Convert divisions tree to SelectItem format
function divisionsToSelectItems(
  tree: DivisionsTree
): SelectItem<Office | string>[] {
  const items: SelectItem<Office | string>[] = [];

  // Add top-level offices
  if (tree.offices?.length) {
    tree.offices.forEach((office) => {
      items.push({
        id: office.id,
        name: office.name,
        keywords: [office.name],
        icon: <OfficeIcon className="h-4 w-4" />,
        data: office
      });
    });
  }

  // Add divisions with their children
  if (tree.children?.length) {
    tree.children.forEach((division) => {
      const divisionItem: SelectItem<Office | string> = {
        id: division.id,
        name: division.name,
        keywords: [division.name],
        children: []
      };

      // Add offices in this division
      if (division.offices?.length) {
        division.offices.forEach((office) => {
          divisionItem.children!.push({
            id: office.id,
            name: office.name,
            keywords: [office.name],
            icon: <OfficeIcon className="h-4 w-4" />,
            data: office
          });
        });
      }

      // Add nested divisions recursively
      if (division.children?.length) {
        const nestedItems = divisionsToSelectItems(
          division as unknown as DivisionsTree
        );
        divisionItem.children!.push(...nestedItems);
      }

      // Only add if has children
      if (divisionItem.children!.length > 0) {
        items.push(divisionItem);
      }
    });
  }

  return items;
}

// Find an item by ID in the tree
function findSelectItemById(
  items: SelectItem<Office | string>[],
  id: string
): SelectItem<Office | string> | null {
  for (const item of items) {
    if (item.id === id) return item;
    if (item.children) {
      const found = findSelectItemById(item.children, id);
      if (found) return found;
    }
  }
  return null;
}

const findOfficeInTree = (
  tree: DivisionsTree | null | undefined,
  officeId: string | null | undefined
): Office | null => {
  if (!tree || !officeId) return null;

  const searchInNode = (node: DivisionsTree): Office | null => {
    // Check offices in current node
    if (node.offices) {
      const office = node.offices.find((o) => o.id === officeId);
      if (office) return office;
    }

    // Search in children
    if (node.children) {
      for (const child of node.children) {
        const found = searchInNode(child);
        if (found) return found;
      }
    }

    return null;
  };

  return searchInNode(tree);
};

export const AgendaDay = ({
  office_id,
  date,
  schedule,
  onSelectItemChange,
  selectedEvent,
  onLocationChange,
  workingLocationId,
  day,
  officeAvailability,
  location,
  isLoadingOfficeAvailability = false,
  isLocationLoading = false,
  optimisticLocation = null,
  isMobile = false,
  orgSlug,
  isCollapsible = false,
  showTodayBadge = false,
  dedicatedDesk = null,
  isDeskReleased = false,
  areDeskReleasesKnown = true,
  areDeskReleasesLoading = false
}: {
  office_id: string | null;
  date: string;
  schedule: AgendaSchedule[] | null;
  // biome-ignore lint/suspicious/noExplicitAny: selectedEvent can be AgendaSchedule or Visit
  selectedEvent: any;
  // biome-ignore lint/suspicious/noExplicitAny: supports both AgendaSchedule and Visit
  onSelectItemChange: (item: any) => void;
  onLocationChange: (location: IWorkingLocationPresenceType) => void;
  workingLocationId: string | undefined;
  day: AgendaDayType | null;
  officeAvailability: OfficeAvailability | null;
  location: IWorkingLocationPresenceType | IWorkingLocation | null;
  isLoadingOfficeAvailability?: boolean;
  isLocationLoading?: boolean;
  optimisticLocation?: AgendaDayType | null;
  isMobile?: boolean;
  orgSlug?: string;
  isCollapsible?: boolean;
  showTodayBadge?: boolean;
  /** The person's dedicated desk, shown on days at its office. */
  dedicatedDesk?: IDedicatedResourceRef | null;
  /** True on a day the desk is released to others while the person is away. */
  isDeskReleased?: boolean;
  /**
   * False until the desk's released days have arrived: until then a released
   * day would read as the person's own desk. A request that failed leaves
   * this false too, because a failure says nothing about the days.
   */
  areDeskReleasesKnown?: boolean;
  /** True while they are still on their way, as opposed to having failed. */
  areDeskReleasesLoading?: boolean;
}) => {
  const router = useRouter();
  const { openModal, closeModal } = useMapModalContext();
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const isWorkplaceRequestsAllowed =
    useWorkplaceRequestsAllowed("workplace_requests");
  const divisions = useDivisionsMenuQuery();

  const { openDuplicateModal } = useReserve();
  const { openEditModal, openCancelModal, setReserveModalData } =
    useReserveModal();

  const selectedOfficeRef = useRef<Office | null>(null);

  const effectiveDay =
    optimisticLocation ||
    (day?.location === WL_PRESENCE_OFFICE ? office_id : day);

  const effectiveLocation =
    optimisticLocation?.location ||
    (typeof location === "string" ? location : location?.location);

  const effectiveWorkingLocationId =
    (optimisticLocation as OptimisticLocation)?.office_id || workingLocationId;

  useEffect(() => {
    const currentOfficeId =
      effectiveLocation === WL_PRESENCE_OFFICE
        ? (office_id ?? effectiveWorkingLocationId ?? null)
        : null;

    if (currentOfficeId && divisions.data?.tree) {
      const office = findOfficeInTree(divisions.data.tree, currentOfficeId);
      selectedOfficeRef.current = office;
    } else if (effectiveLocation !== WL_PRESENCE_OFFICE) {
      // Clear the ref when location changes to remote or OOO
      selectedOfficeRef.current = null;
    }
  }, [
    office_id,
    effectiveWorkingLocationId,
    effectiveLocation,
    divisions.data?.tree
  ]);

  const handleOpenEditModal = (schedule: ISchedule) => {
    openEditModal(schedule, true, selectedOfficeRef.current ?? undefined);
  };

  const handleOpenCancelModal = (schedule: ISchedule) => {
    openCancelModal([schedule]);
  };

  // usually the same office for all the reservations
  // TBD: group schedule items by office later
  const defaultOffice = office_id;

  const isOOO = schedule?.some((item) => {
    // @ts-expect-error - Expected error due to missing property
    return item.location === WL_PRESENCE_OOO;
  });

  // when ooo or remote assigned after the office is selected,
  // office is not being removed from the schedule
  // so we need to hide the office card
  const hideOfficeCard =
    effectiveLocation === WL_PRESENCE_REMOTE ||
    effectiveLocation === WL_PRESENCE_OOO;

  const fallbackOfficeSelectValue = defaultOffice ?? "";

  // Calculate the defaultValue for OfficeSelect
  let selectValue = "";
  // Handle all three types of locations:
  // 1. If it's remote or OOO, use the location value directly
  if (
    effectiveLocation === WL_PRESENCE_REMOTE ||
    effectiveLocation === WL_PRESENCE_OOO
  ) {
    selectValue = effectiveLocation;
  }
  // 2. If it's office, use the office ID
  else if (
    effectiveLocation === WL_PRESENCE_OFFICE &&
    (effectiveWorkingLocationId || office_id)
  ) {
    selectValue = office_id ?? effectiveWorkingLocationId ?? "";
  }
  // 3. Otherwise, use fallback
  else if (effectiveWorkingLocationId) {
    selectValue = effectiveWorkingLocationId;
  } else {
    selectValue = fallbackOfficeSelectValue;
  }
  const isPastDate = isPast(parseApiDate(date)) && !isToday(parseApiDate(date));

  const [isCollapsed, setIsCollapsed] = useState(isCollapsible);

  useEffect(() => {
    setIsCollapsed(isCollapsible);
  }, [isCollapsible]);

  const collapsedLocationLabel = useMemo(() => {
    if (effectiveLocation === WL_PRESENCE_REMOTE) return "Remote";
    if (effectiveLocation === WL_PRESENCE_OOO) return "Out of Office";
    if (effectiveLocation === WL_PRESENCE_OFFICE) {
      const officeId = office_id ?? effectiveWorkingLocationId ?? null;
      const office = findOfficeInTree(divisions.data?.tree, officeId);
      return office?.name ?? "";
    }
    return "";
  }, [
    effectiveLocation,
    office_id,
    effectiveWorkingLocationId,
    divisions.data?.tree
  ]);

  const policy = day?.policy;

  // The person's own dedicated desk, on a day at its office: nothing to
  // reserve, the desk is theirs. On a day they released it to others the
  // agenda treats them like anyone else: no desk card, the reserve prompt.
  const isAtDeskOffice =
    !!dedicatedDesk?.office_id &&
    !hideOfficeCard &&
    !policy &&
    selectValue === dedicatedDesk.office_id;
  // Past days count too, back to the day the desk became theirs.
  const deskOfficeTimeZone = dedicatedDesk?.office_id
    ? authenticatedUser.offices?.byId[dedicatedDesk.office_id]?.time_zone
    : undefined;
  // A day the desk is the person's, whether or not they have released it.
  const isDeskDay =
    isAtDeskOffice &&
    !!dedicatedDesk &&
    // "Today" is the desk office's day, not the viewer's: the question is
    // about the desk, and a desk whose office has not reached the day yet is
    // not theirs for it. Only the fallback for a desk with no known start
    // reads it; see isDeskDedicatedOn on which calendar each day comes from.
    isDeskDedicatedOn(dedicatedDesk, date, officeToday(deskOfficeTimeZone));
  const showDedicatedDesk =
    isDeskDay && areDeskReleasesKnown && !isDeskReleased;
  // Which of the two belongs on a desk day depends on whether the desk is
  // released, so while the desk or its releases are on their way neither is
  // shown. Otherwise the day flashes a prompt to reserve a desk the person
  // already has: on first load, and again on every week change, because the
  // releases query is keyed on the week being looked at. Before the desk
  // itself has arrived there is no telling which days are its days, so every
  // day waits; that is one request, once. A request that failed is a
  // different matter: the day falls through to the reserve prompt, which at
  // least leaves something to act on.
  const isDeskDayPending =
    areDeskReleasesLoading && (isDeskDay || !dedicatedDesk);
  // The desk opens in the details column like a reservation would, and a
  // second click closes it again.
  const isDedicatedDeskSelected =
    !!dedicatedDesk &&
    isDedicatedDeskSelection(selectedEvent) &&
    selectedEvent.desk.id === dedicatedDesk.id &&
    selectedEvent.date === date;
  const toggleDedicatedDeskDetails = dedicatedDesk
    ? () =>
        onSelectItemChange(
          isDedicatedDeskSelected
            ? null
            : selectDedicatedDesk(dedicatedDesk, date)
        )
    : undefined;

  const handleReservationClick = () => {
    // Use the selected office from OfficeSelect if available, otherwise use office_id from props
    const selectedOffice = selectedOfficeRef.current;
    const officeToUse =
      selectedOffice ||
      (office_id ? authenticatedUser.offices?.byId[office_id] : null);
    if (!isMobile) {
      openModal({
        date: date,
        officeId: officeToUse?.id ?? office_id ?? undefined,
        resourceId: undefined,
        floorId: undefined,
        defaultTab: "desks"
      });
    } else {
      setReserveModalData({
        reserveModal: true,
        resourceType: "desk",
        start: parseApiDate(date),
        office: officeToUse ?? undefined
      });
    }
  };

  if (isCollapsed && !isMobile) {
    return (
      <button
        type="button"
        onClick={() => setIsCollapsed(false)}
        className={cn(
          "-mt-4 flex h-9 w-full items-center justify-between gap-2 px-1 text-left text-sm first:mt-0",
          { "opacity-70": isLocationLoading }
        )}
        aria-label={`Expand ${format(parseApiDate(date), "EEEE")}`}
      >
        <div className="flex items-center gap-1">
          <ChevronRight className="h-4 w-4 text-neutral-500" />
          <DayDate apiDate={date} />
        </div>
        <span className="truncate text-neutral-500 text-sm">
          {collapsedLocationLabel}
        </span>
      </button>
    );
  }

  return (
    <div className={cn("flex flex-col", { "opacity-70": isLocationLoading })}>
      <div className="flex items-center justify-between space-x-2 px-1 text-sm">
        {!isMobile ? (
          <div className={cn("relative flex shrink-0 items-center gap-1")}>
            {isCollapsible ? (
              <button
                type="button"
                onClick={() => setIsCollapsed(true)}
                className="flex items-center gap-1 text-left"
                aria-label={`Collapse ${format(parseApiDate(date), "EEEE")}`}
              >
                <ChevronDownIcon className="h-4 w-4 text-neutral-500" />
                <DayDate apiDate={date} className="z-11" />
              </button>
            ) : (
              <DayDate apiDate={date} className="z-11" />
            )}
            {showTodayBadge && (
              <span className="font-semibold text-primary text-xs tracking-wider uppercase">
                Today
              </span>
            )}
            {!isPastDate && (
              <button
                className={cn("group flex items-center justify-center", {
                  "cursor-pointer ": !isPastDate
                })}
                aria-label="Open map reservation"
                onClick={() => {
                  if (!isPastDate) {
                    openModal({
                      date: date,
                      officeId:
                        selectedOfficeRef.current?.id ?? office_id ?? undefined,
                      reservationModalIsOpen: true,
                      onSuccess: () => {
                        closeModal();
                      }
                    });
                  }
                }}
              >
                <Plus className="h-3.5 w-3.5 text-neutral-700 transition-all group-hover:animate-elastic-spin group-hover:text-primary" />
              </button>
            )}
          </div>
        ) : (
          <div
            className={"cursor-pointer font-bold"}
            // Don't use this as a trigger on mobile
            // onClick={() => {
            //   const query: Record<string, string> = {};
            //   if (date) query.date = `${date}T12:00:00`;
            //   if (office_id) query.officeId = office_id;

            //   router.push({
            //     pathname: "/map",
            //     query
            //   });
            // }}
          >
            Working location
          </div>
        )}
        <div className="flex min-w-0 flex-1 items-center justify-end">
          <div className="flex min-w-0 items-center justify-end gap-1">
            {/* If policy is requested, don't allow user to change location */}
            {divisions.data && !policy ? (
              <AgendaOfficeSelect
                divisions={divisions.data}
                selectValue={selectValue}
                isPastDate={isPastDate}
                policy={policy}
                onValueChange={async (value) => {
                  // Update the selected office ref when office changes
                  if (
                    value === WL_PRESENCE_OFFICE ||
                    (value &&
                      value !== WL_PRESENCE_REMOTE &&
                      value !== WL_PRESENCE_OOO)
                  ) {
                    const office = findOfficeInTree(
                      divisions.data?.tree,
                      value
                    );
                    selectedOfficeRef.current = office;
                  } else {
                    selectedOfficeRef.current = null;
                  }

                  await onLocationChange(value as IWorkingLocationPresenceType);
                }}
              />
            ) : (
              <div className="h-3 w-1 bg-transparent" />
            )}
            {policy && (
              <PolicyButton
                policy={policy}
                status={effectiveLocation}
                date={date}
              />
            )}
          </div>
        </div>
      </div>
      <div className={cn("flex flex-col gap-1")}>
        {showDedicatedDesk && dedicatedDesk && (
          <DedicatedDeskCard
            deskName={dedicatedDesk.name}
            onSelect={toggleDedicatedDeskDetails}
            isSelected={isDedicatedDeskSelected}
          />
        )}
        {!policy && schedule && schedule.length > 0 ? (
          schedule?.map((item) => {
            // const canEdit = getCurrentUserCanEdit(item, authenticatedUser);
            const isSelected = selectedEvent?.id === item.id;
            return (
              <CheckInCardOnWeb
                key={item.id}
                reservation={item}
                onClick={() => {
                  if (isSelected) {
                    onSelectItemChange(null);
                    return;
                  }
                  onSelectItemChange(item);
                }}
                layoutProps={{
                  isSelected
                }}
                onEdit={async () => {
                  // The agenda endpoint returns true-UTC times, while the
                  // reserve/edit flows expect the office wall-clock "fake Z"
                  // convention of the schedules endpoints [sc-8721]
                  const wallClockItem = scheduleToOfficeWallClock(
                    item,
                    item.office?.time_zone
                  );
                  if (!isMobile) {
                    const editData = await getDuplicateReservationData({
                      item: wallClockItem,
                      user: authenticatedUser,
                      isWorkplaceRequestsAllowed,
                      api,
                      shouldSelectResource: true,
                      authenticatedUser: authenticatedUser
                    });

                    const defaultTab =
                      item.resource.type === "meeting_room"
                        ? "rooms"
                        : item.resource.type === "parking_lot" ||
                            item.resource.type === "parking_space"
                          ? "other"
                          : "desks";

                    openModal({
                      date: date,
                      officeId:
                        selectedOfficeRef.current?.id ?? office_id ?? undefined,
                      reservationModalIsOpen: true,
                      duplicateData: editData,
                      editSchedule: wallClockItem,
                      defaultTab,
                      onSuccess: () => {
                        closeModal();
                      }
                    });
                  } else {
                    await handleOpenEditModal(wallClockItem);
                  }
                }}
                onCancel={async () => {
                  await handleOpenCancelModal(item);
                }}
                onDuplicate={async () => {
                  // Same true-UTC → office wall-clock conversion as onEdit
                  const wallClockItem = scheduleToOfficeWallClock(
                    item,
                    item.office?.time_zone
                  );
                  if (!isMobile) {
                    const duplicateData = await getDuplicateReservationData({
                      item: wallClockItem,
                      user: authenticatedUser,
                      isWorkplaceRequestsAllowed,
                      api,
                      shouldSelectResource: true,
                      authenticatedUser: authenticatedUser
                    });

                    openModal({
                      date: date,
                      officeId:
                        selectedOfficeRef.current?.id ?? office_id ?? undefined,
                      reservationModalIsOpen: true,
                      duplicateData,
                      defaultTab: getMapModalTab(item.resource.type),
                      onSuccess: () => {
                        closeModal();
                      }
                    });
                  } else {
                    setTimeout(() => {
                      openDuplicateModal(wallClockItem).then();
                    }, 200);
                  }
                }}
              />
            );
          })
        ) : showDedicatedDesk || isDeskDayPending ? null : (
          <ReservationCard
            isPastDate={isPastDate}
            isInOffice={
              !hideOfficeCard &&
              effectiveDay &&
              typeof effectiveDay === "object" &&
              "office" in effectiveDay
                ? !!effectiveDay.office
                : day?.location === WL_PRESENCE_OFFICE
            }
            onReservationClick={handleReservationClick}
            disabled={isPastDate || !!policy}
            isLoadingDesksAvailability={isLoadingOfficeAvailability}
            availableDesks={officeAvailability?.available_desks}
            totalDesks={officeAvailability?.total_desks}
          />
        )}
        {day?.visits?.map((visit) => {
          const isVisitSelected = selectedEvent?.id === visit.id;
          return (
            <VisitCard
              key={visit.id}
              visit={visit}
              isSelected={isVisitSelected}
              onCardClick={() => {
                if (isVisitSelected) {
                  onSelectItemChange(null);
                  return;
                }
                onSelectItemChange(visit);
              }}
            />
          );
        })}
      </div>
    </div>
  );
};

export const AgendaOfficeSelect = ({
  divisions,
  selectValue,
  isPastDate,
  policy,
  onValueChange
}: {
  divisions: { tree: DivisionsTree };
  selectValue: string;
  isPastDate: boolean;
  policy?: AgendaDayType["policy"];
  onValueChange: (value: string) => void;
}) => {
  const items = useMemo<SelectItem<Office | string>[]>(() => {
    if (!divisions?.tree) return [];

    const officeItems = divisionsToSelectItems(divisions.tree);

    // Add Remote and OOO options at the end
    return [
      ...officeItems,
      {
        id: WL_PRESENCE_REMOTE,
        name: "Remote",
        keywords: ["remote", "home", "wfh"],
        icon: <HomeIcon className="h-4 w-4" />,
        data: WL_PRESENCE_REMOTE
      },
      {
        id: WL_PRESENCE_OOO,
        name: "Out of Office",
        keywords: ["ooo", "out", "away", "vacation"],
        icon: <OOOIcon className="h-4 w-4" />,
        data: WL_PRESENCE_OOO
      }
    ];
  }, [divisions]);

  const selectedItem = useMemo(() => {
    if (!selectValue || items.length === 0) return null;
    return findSelectItemById(items, selectValue);
  }, [items, selectValue]);

  const handleSelect = (item: SelectItem<Office | string> | null) => {
    const newValue = item?.id ?? "";
    onValueChange(newValue);
  };

  const isDisabled = isPastDate || !!policy;

  const triggerContent = selectedItem ? (
    <span className="min-w-0 truncate font-inter">{selectedItem.name}</span>
  ) : (
    <span className="font-inter text-muted-foreground">Select an office</span>
  );

  if (isDisabled) {
    return (
      <span className="flex h-9 min-w-0 max-w-[175px] items-center gap-1 truncate text-sm opacity-50 md:max-w-[258px]">
        {triggerContent}
      </span>
    );
  }

  return (
    <UnifiedSelect
      items={items}
      value={selectedItem}
      onSelect={handleSelect}
      searchable
      hierarchical
      searchPlaceholder="Search..."
      placeholder="Select an office"
      disabled={false}
      trigger={
        <span className="flex h-9 min-w-0 max-w-[175px] cursor-pointer items-center gap-1 truncate text-sm md:max-w-[258px]">
          {triggerContent}
          <ChevronDownIcon
            className={cn(
              "size-4 shrink-0",
              !selectedItem ? "text-muted-foreground" : ""
            )}
          />
        </span>
      }
    />
  );
};

const PolicyButton = ({
  policy,
  status,
  date
}: {
  policy: AgendaDayType["policy"];
  date: string;
  status: IWorkingLocationPresenceType | undefined;
}) => {
  const statusName =
    status === WL_PRESENCE_REMOTE
      ? "Remote"
      : status === WL_PRESENCE_OOO
        ? "Out of Office"
        : null;
  const router = useRouter();
  if (!policy) {
    return null;
  }
  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            className="cursor-pointer py-3"
            onClick={() =>
              router.push({
                pathname: buildUrl(SETTINGS_ROUTES.attendance),
                query: {
                  start: format(parseDateOnlyString(date), "yyyy-MM-dd")
                }
              })
            }
          >
            {statusName} ({capitalize(policy.type)} Policy)
          </button>
        </TooltipTrigger>
        <TooltipContent>My Schedules Requests</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
